// chartSeries.js
// Legend labels for chart series, and whether a legend is worth drawing at all.
//
// A chart artifact's series labels are whatever the generated matplotlib code
// named them. A series that was never given a label falls through to
// matplotlib's internal handle — `_child0`, `Group 0`, a bare underscore — which
// says nothing about the data. New charts are labelled properly at source, but
// stored history cannot be rewritten, so every renderer maps its raw labels
// through here on the way to plotly.
//
// The rule has two halves, and both are DISPLAY-ONLY: the caller renames a COPY
// and never writes the result back onto the stored spec.
//   - ONE series with a placeholder label: a legend reading "_child0" carries no
//     information whatsoever, so the legend is dropped entirely.
//   - SEVERAL series: the legend still tells them apart, so each placeholder
//     becomes "Series N" by its 1-based position. A label the chart's author
//     actually chose is left exactly as written, even sitting next to a
//     placeholder.
//
// Everything here is pure — no React, no plotly.

const PLACEHOLDER_PATTERNS = [/^_child\d+$/, /^Group \d+$/];

// A missing, blank or machine-generated label. The leading-underscore case is
// matplotlib's own convention for "hide this artist from the legend", so any
// such label reaching the frontend is a placeholder by definition.
export const isPlaceholderSeriesLabel = (label) => {
  if (typeof label !== 'string') return true;
  const trimmed = label.trim();
  if (trimmed === '') return true;
  return trimmed.startsWith('_') || PLACEHOLDER_PATTERNS.some((pattern) => pattern.test(trimmed));
};

// Takes a chart's series labels in trace order and returns the labels to show
// plus whether the legend should be drawn. One call per render feeds both the
// trace names and plotly's `showlegend`, so the two can never disagree.
//
// Placeholders are renamed even in the single-series case: the name survives in
// the hover tooltip after the legend is gone, and "_child0" reads no better
// there than it does in a legend.
export const resolveSeriesLabels = (rawLabels) => {
  const raw = Array.isArray(rawLabels) ? rawLabels : [];
  const placeholders = raw.map(isPlaceholderSeriesLabel);
  return {
    labels: raw.map((label, index) => (placeholders[index] ? `Series ${index + 1}` : label)),
    hideLegend: raw.length === 1 && placeholders[0],
  };
};
